<template>
  <AmLoading 
    v-if="loading"
    :isLoading="loading"
    class="mx-auto pb-10"
  />
  <div 
    v-else 
    v-click-outside="handleClickOutside"
    class="mb-10"
  >
    <AmToast
      v-if="showAlert"
      :message="alertMessage"
      :duration="3000"
      margin="mt-28 ml-4"
      position="top-right"
      closable
    />
    <AmUploader
      v-if="appStore.screenMode !== ScreenMode.EDITING && appStore.screenMode !== ScreenMode.DRAFT && returningToCreationStep !== true"
      v-model="state.logo"
      accept="image/jpeg, image/png"
      :label="$t(`createAppForm.imagesApp.form.iconApp.label`)"
      :hint="$t(`createAppForm.imagesApp.form.iconApp.hint`)"
      :inputMessage="$t(`createAppForm.imagesApp.form.iconApp.inputMessage`)"
      :hasError="
        (v$.logo.$dirty && v$.logo.fileSize.$invalid && !v$.logo.required.$invalid) 
          || (v$.logo.$dirty && v$.logo.required.$invalid && v$.logo.fileSize.$invalid)"
    />

    <AmUploader
      v-if="appStore.screenMode === ScreenMode.EDITING || appStore.screenMode === ScreenMode.DRAFT || returningToCreationStep == true"
      v-model="state.logo"
      dataTestName="edit-app-form-logo"
      accept="image/jpeg, image/png"
      :label="$t(`createAppForm.imagesApp.form.iconApp.label`)"
      :hint="$t(`createAppForm.imagesApp.form.iconApp.hint`)"
      :inputMessage="$t(`createAppForm.imagesApp.form.iconApp.inputMessage`)"
      :initialImage="appStore.app.logo ? appStore.app.logo : null"
      :hasError="
        (v$.logo.$dirty && v$.logo.fileSize.$invalid && !v$.logo.required.$invalid) 
          || (v$.logo.$dirty && v$.logo.required.$invalid && v$.logo.fileSize.$invalid)"
      @change="hasEdition = true"
    />
    <span 
      v-if="v$.logo.$dirty && v$.logo.fileSize.$invalid && !v$.logo.required.$invalid"
      class="text-error100"
    >{{ $t('form.imagesApp.feedbacks.imageSize') }}</span>
    <span 
      v-if="v$.logo.$dirty && v$.logo.required.$invalid && v$.logo.fileSize.$invalid"
      class="text-error100"
    >{{ $t('form.imagesApp.feedbacks.imageRequired') }}</span>

    <div
      v-if="appStore.screenMode === ScreenMode.CREATION || appStore.screenMode === ScreenMode.DRAFT"
      class="flex w-full flex-col-reverse md:flex-row md:justify-end md:items-center mt-6 gap-2">
      <AmButton
        v-if="!isEditMode"
        variant="maxGhost"
        :label="
          isEditMode
            ? $t('form.imagesApp.actions.cancel')
            : $t(`form.imagesApp.actions.return`)
        "
        :size="mq.current === 'mobile' ? 'fullWidth' : 'xxlarge'"
        :class="[{ 'py-3': mq.current === 'mobile' }]"
        @click="isEditMode ? cancelEdit() : ActionBackToAboutApp()"
      />

      <AmButton
        variant="maxPrimary"
        data-test="submit-images-app-button"
        :label="
          isEditMode
            ? $t('form.imagesApp.actions.saveAndSendToAnalysis')
            : $t(`form.imagesApp.actions.advanced`)
        "
        :size="mq.current === 'mobile' ? 'fullWidth' : 'xxlarge'"
        :disabled="!formValid"
        :class="[{ 'py-3': mq.current === 'mobile' }]"
        @eventKeyAmButton="submitForm"
        @click="submitForm"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, inject, onBeforeMount, ref, watch, reactive } from 'vue'
import { AmButton, AmUploader, AmLoading, AmToast } from '@appmax_npm/ds-appmax-v3'
import { required} from '@vuelidate/validators'
import { useAppsStore } from '@/stores/apps'
import { useHttpStore } from '@/stores/http'
import { useReturnBarStore } from '@/stores/returnBar'
import { useRouter } from 'vue-router'
import useVuelidate from '@vuelidate/core'
import { useI18n } from 'vue-i18n'
import { useAppStore, ScreenMode } from '@/stores/modules/app'
import _ from 'lodash'

const emit = defineEmits(['formSubmitIsFinish'])
const { app } = defineProps({
  app: {
    type: Object,
    required: false
  }
})

const appStore = useAppStore()
const returnBarStore = useReturnBarStore()

const mq = inject('mq')
const router = useRouter()
const appsStore = useAppsStore()
const httpStore = useHttpStore()
const { t } = useI18n()
const hasEdition = ref(false)
const showAlert = ref(false)
const alertMessage = ref(null)
const returningToCreationStep = ref(false)

const isEditMode = computed(() => {
  return !!app
})
const loading = ref(false)

const state = reactive({
  logo: null,
})

const rules = {
  logo: {
    required,
    fileSize: (value) => {
      if (value) {
        const maxSize = 2048 * 1024
        return value[0].size <= maxSize
      }

      return false
    },
  }
}

const v$ = useVuelidate(rules, state)
const formValid = ref(false)

const validForm = async () => {
  let validations = {}
    
  validations = {
    logo: await v$.value.logo.$validate()
  }

  return Object.values(validations).every((validation) => validation)
}

watch(
  () => state,
  async () => {
    formValid.value = await validForm()
    appStore.validateEditForm = formValid.value && !_.isEmpty(state.logo)
    appStore.modificatedSteps = appStore.addStepModificated('imagesApp')
  },
  { deep: true }
)

const cancelEdit = () => {
  emit('formSubmitIsFinish', false)
}

const submitForm = async () => {
  loading.value = true

  const data = {
    update_section_id: 3,
    logo: state.logo[0]
  }

  if (appStore.screenMode === ScreenMode.CREATION || appStore.screenMode === ScreenMode.DRAFT) {
    await httpStore.sendImagesApp(appStore.app.id, data).then(() => {
      handleClickOutside()
      router.push({ name: 'create-app_config-app' })
    }).catch(async () => {
      loading.value = false
      showAlert.value = true

      alertMessage.value = {
        alertType: 'warning',
        title: computed(() => t('form.imagesApp.feedbacks.form.error.title')),
        description: computed(() =>
          t('form.imagesApp.feedbacks.form.error.description')
        )
      }

      formValid.value = false
    })

    return
  }

  httpStore.editImagesApp(appsStore.appID, data).then(() => {
    handleClickOutside()
    emit('formSubmitIsFinish', false)
    loading.value = false
  }).catch(() => {
    emit('formSubmitIsFinish', true)
    loading.value = false
  })
}

function handleClickOutside() {
  appStore.app.logo = state.logo[0]
}

const ActionBackToAboutApp = () => {
  router.push({ name: 'create-app_about-app', state: { parameter: true } });
}

onBeforeMount(async () => {
  returningToCreationStep.value = window.history.state.parameter

  if (appStore.screenMode === ScreenMode.DRAFT || appStore.screenMode === ScreenMode.EDITING || returningToCreationStep.value == true) {
    state.logo = appStore.app.logo
  }

  if (appStore.screenMode === ScreenMode.DRAFT || appStore.screenMode === ScreenMode.CREATION) {
    returnBarStore.title = t(`createAppForm.imagesApp.title`)
  }
});
</script>